import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Select, Divider, Drawer, notification } from 'antd';
import SellerActions from '@Actions/sellerActions';
import Helpers from '@Apis/helpers';
import H3Text from '@Uilib/h3Text';
import H3Image from '@Uilib/h3Image';
import closeIcon from '@Images/icons/icon-close-blue.png';
import VendorForm from '@Components/Common/VendorForm';
import PRZSelect from '../UI/PRZSelect';
import useDebouncedEffect from '../../../hooks/useDebouncedEffect';
import './style.scss';

const { Option } = Select;

const SelectSeller = ({
  selectedSeller, onChange, disabled, showError, title, hideTitle,
  containerClass, inputClassName, labelClassName, showAddVendor,
  getPopupContainer, notRequired, dropdownAlign, tenantId,
  showAllSeller, mapSellerId, marketplace, isSubcontractor,
  transporter, placeholder, isMarketplaceSeller, isMultiple,
  dontIncludesThisSellerIdsInList,
}) => {
  const [showNewVendorModal, setShowNewVendorModal] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');

  const dispatch = useDispatch();
  const location = useLocation();
  const sellers = useSelector((state) => state.SellerReducers.sellers);
  const user = useSelector((state) => state.UserReducers.user);
  const getSellersLoading = useSelector((state) => state.SellerReducers.getSellersLoading);

  const effectiveTenantId = tenantId || user?.tenant_info?.tenant_id;

  const hasFetchedSellers = useRef(false);

  const getSellers = (keyword = '', normalizedSelectedSeller = '') => {
    dispatch(
      SellerActions.getSellers(
        keyword,
        effectiveTenantId,
        1,
        1000,
        normalizedSelectedSeller,
        () => {},
        true,
        isSubcontractor,
        '',
        isMarketplaceSeller,
        '',
        dontIncludesThisSellerIdsInList
      )
    );
  };

  useEffect(() => {
    if (effectiveTenantId && !hasFetchedSellers.current) {
      hasFetchedSellers.current = true;
      getSellers('', selectedSeller || '');
    }
  }, [effectiveTenantId]);

  useDebouncedEffect(() => {
    if (searchKeyword !== '') {
      getSellers(searchKeyword, '');
    }
  }, 500, [searchKeyword]);

  const permissionForCreateVendor = Helpers.getPermission(
    Helpers.permissionEntities.VENDORS,
    Helpers.permissionTypes.CREATE,
    user,
  );

  return (
    <div className={`${containerClass || ''} select-vendor__wrapper`}>
      <Drawer
        open={showNewVendorModal}
        onClose={() => setShowNewVendorModal(false)}
        width="720px"
        destroyOnClose
      >
        <div className="custom-drawer__header-wrapper">
          <div className="custom-drawer__header" style={{ width: '680px' }}>
            <H3Text text="Add New Vendor" className="custom-drawer__title" />
            <H3Image
              src={closeIcon}
              className="custom-drawer__close-icon"
              onClick={() => setShowNewVendorModal(false)}
            />
          </div>
        </div>
        <VendorForm
          callback={(createdSeller) => {
            getSellers(); // Refresh list
            const foundSeller = sellers?.sellers?.find(
              (seller) => seller?.seller_id === createdSeller?.seller_id
            );
            onChange(foundSeller);
            setShowNewVendorModal(false);
          }}
          subcontractorForm
          isMarketplaceSellerProp={isMarketplaceSeller}
        />
      </Drawer>

      {!hideTitle && (
        <div className={labelClassName || 'orgFormLabel'}>
          {title || 'Select Vendor'}
          {!notRequired && <span style={{ color: 'red' }}>{'  *'}</span>}
        </div>
      )}

      <div className={inputClassName || 'orgFormInput input'}>
        <PRZSelect
          onDropdownVisibleChange={setDropdownOpen}
          value={selectedSeller}
          filterOption={false}
          getPopupContainer={getPopupContainer ? (triggerNode) => triggerNode.parentNode : false}
          placeholder={placeholder || (transporter ? 'Select Transporter..' : 'Select vendor..')}
          disabled={disabled}
          loading={getSellersLoading}
          maxTagCount="responsive"
          mode={isMultiple ? 'multiple' : undefined}
          showSearch
          onSearch={(value) => setSearchKeyword(value)}
          onSelect={() => getSellers()} // no selectedSeller
          onChange={(value) => {
            if (isMultiple) {
              onChange(value);
            } else {
              const selected = sellers?.sellers?.find(
                (seller) =>
                  (mapSellerId ? seller?.seller_id : seller?.tenant_seller_id) === value
              );
              onChange(selected);
            }
          }}
          dropdownRender={
            showAddVendor
              ? (menu) => (
                  <>
                    {menu}
                    <Divider style={{ margin: 0 }} />
                    <div
                      className="add__vendor"
                      onClick={() => {
                        if (permissionForCreateVendor) {
                          setShowNewVendorModal(true);
                        } else {
                          notification.error({
                            message: 'You do not have permission to create a new vendor.',
                            duration: 4,
                            placement: 'top',
                          });
                        }
                      }}
                    >
                      + Add Vendor
                    </div>
                  </>
                )
              : undefined
          }
        >
          {showAllSeller && <Option key="" value="">All Vendors</Option>}
          {sellers?.sellers
            ?.filter((seller) =>
              marketplace
                ? seller?.seller_info?.is_marketplace_seller
                : !seller?.seller_info?.is_marketplace_seller
            )
            ?.filter((seller) => (transporter ? seller?.seller_info?.is_transporter : true))
            ?.filter((seller) => seller?.is_active)
            ?.map((item) => (
              <Option
                key={mapSellerId ? item.seller_id : item.tenant_seller_id}
                value={mapSellerId ? item.seller_id : item.tenant_seller_id}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <span className="seller-name">{item?.seller_info?.internal_slr_code}</span>
                    <span className="separator"> - </span>
                    <span className="customer-name">{item?.seller_info?.seller_name}</span>
                    {item?.seller_info?.office_address_details?.city && (
                      <span className="city-info">
                        ({item?.seller_info?.office_address_details.city})
                      </span>
                    )}
                  </div>
                </div>
              </Option>
            ))}
        </PRZSelect>

        {showError && (
          <div className="input-error">
            *Please select a {transporter ? 'transporter' : 'vendor'}
          </div>
        )}
      </div>
    </div>
  );
};

export default SelectSeller;
